import { css } from '@emotion/css';
import { useStore, useUpdate } from 'dg-web-shared/lib/Flux';
import { makeEnforcementUrl } from '../Http';
import { texts } from '../i18n';
import * as AuthTokenState from '../state/AuthTokenState';
import { ResetPasswordDeck } from './ResetPasswordDeck';
import { Spinner } from './Spinner';
import { Colors, Typo } from './vars';
import { Box, Button, Stack } from '@mui/material';
import ParkingCheckLogo from '../../assets/parking-check-logo.svg';
import {
    RequestMethod,
    RequestStatus,
    useServerSuccessEffect,
    useServerWrite,
} from 'dg-web-shared/lib/hooks/ServerStateHooks.ts';
import { useForm, useWatch } from 'react-hook-form';
import { ReactHookFormTextField } from 'dg-web-shared/common/components/material-ui/react-hook-form-fields/ReactHookFormTextField.tsx';
import { ValidationData } from 'dg-web-shared/lib/forms/FormValidationHelpers.tsx';
import { basicAuthHeader } from 'dg-web-shared/lib/Http.ts';
import { useLanguage } from 'dg-web-shared/lib/Localization.tsx';
import {
    devEnvs,
    getDevEnvName,
    getEnvChangeName,
} from 'dg-web-shared/common/utils/DevEnvironments.tsx';
import { useEffect } from 'react';
import {
    envIsProduction,
    envIsTest,
    envName,
} from 'dg-web-shared/lib/Environment.ts';

export interface SessionValidity {
    token: string | null;
}

interface LoginPayload {
    username: string;
    password: string;
}

interface LoginFormValues {
    [LoginFormValuesFields.username]: string;
    [LoginFormValuesFields.password]: string;
}

const enum LoginFormValuesFields {
    username = 'username',
    password = 'password',
}

export function Login() {
    const { storeState } = useStore(store => ({
        authToken: AuthTokenState.get(store),
    }));
    const { control, handleSubmit, formState } = useForm<LoginFormValues>({
        defaultValues: {
            username: '',
            password: '',
        },
    });
    const username = useWatch({
        control,
        name: LoginFormValuesFields.username,
    });
    const [loginState, sendLogin] = useServerWrite<
        LoginPayload,
        SessionValidity,
        ValidationData
    >(({ username, password }) => ({
        url: makeEnforcementUrl('auth-token'),
        method: RequestMethod.GET,
        headers: {
            Authorization: basicAuthHeader(username, password),
        },
    }));

    const update = useUpdate();
    useServerSuccessEffect(loginState, data => {
        if (data.token) {
            update(store =>
                AuthTokenState.stateWrite(store, {
                    authToken: data.token,
                }),
            );
        }
    });

    const envChangeName = getEnvChangeName(username);
    useEffect(() => {
        if (!envChangeName) return;
        if (envChangeName === 'test' && !envIsTest()) {
            window.location.href = 'https://enforcement.test.digitalparking.ch';
        } else if (
            devEnvs.includes(envChangeName) &&
            envName() !== envChangeName
        ) {
            window.location.href = `https://enforcement.${envChangeName}.digitalparking.dev/`;
        } else if (envChangeName === 'prod' && !envIsProduction()) {
            window.location.href = 'https://check.digitalparking.ch/';
        }
    }, [envChangeName]);
    const { language } = useLanguage();

    if (storeState.authToken.authToken === null) {
        return <Spinner />;
    }
    return (
        <Box
            sx={{
                background: Colors.white,
                height: '100%',
                width: '100%',
                overflow: 'hidden',
                position: 'relative',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
            }}
        >
            <Box
                sx={{
                    background: Colors.greyBackground,
                    width: '100%',
                }}
            >
                <Box
                    sx={theme => ({
                        padding: `${theme.spacing(3)} ${theme.spacing(4)}`,
                    })}
                >
                    <img src={ParkingCheckLogo} />
                </Box>
            </Box>
            <Box
                sx={{
                    background: Colors.white,
                    width: '100%',
                    flexGrow: 1,
                }}
            >
                <Stack
                    sx={theme => ({
                        padding: `${theme.spacing(6)} ${theme.spacing(
                            4,
                        )} ${theme.spacing(4)}`,
                    })}
                    spacing={1}
                >
                    <Box>
                        <ReactHookFormTextField<LoginFormValues>
                            label={texts[language].username}
                            name={LoginFormValuesFields.username}
                            control={control}
                            rules={{
                                minLength: {
                                    value: 1,
                                    message: '',
                                },
                                required: true,
                            }}
                            fullWidth
                        />
                    </Box>
                    <Box>
                        <ReactHookFormTextField<LoginFormValues>
                            type="password"
                            label={texts[language].password}
                            name={LoginFormValuesFields.password}
                            control={control}
                            rules={{
                                minLength: {
                                    value: 1,
                                    message: '',
                                },
                                required: true,
                            }}
                            fullWidth
                        />
                    </Box>
                    {loginState.status === RequestStatus.ERROR && (
                        <Box
                            className={css({
                                ...Typo.robotoLight,
                                paddingLeft: '16px',
                                color: Colors.red,
                            })}
                        >
                            {texts[language].wrongPw}
                        </Box>
                    )}
                </Stack>
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                    }}
                >
                    <Button
                        variant="contained"
                        color="primary"
                        type="submit"
                        disabled={!formState.isValid}
                        onClick={handleSubmit(fields => {
                            sendLogin({
                                username:
                                    fields[
                                        LoginFormValuesFields.username
                                    ].trim(),
                                password:
                                    fields[LoginFormValuesFields.password],
                            });
                        })}
                        sx={{ marginBottom: 5 }}
                    >
                        {`${texts[language].login}${getDevEnvName()}`}
                    </Button>
                </Box>
            </Box>
            <ResetPasswordDeck suggestedUsername={username} />

            <div id="slidein" />
        </Box>
    );
}
